/**
 * ResetPasswordPage — wachtwoord-reset flow voor zowel medewerkers als klanten.
 *
 * Wordt bereikt via de link in de reset-email:
 *   /reset-password?token=X          → medewerker (auth.py endpoint)
 *   /klant/reset-password?token=X    → klant portal (klant_portal.py endpoint)
 *
 * Bepaalt de juiste API op basis van de huidige URL zodat we niet 2×
 * hetzelfde formulier hoeven te bouwen.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, CheckCircle2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isKlant = location.pathname.startsWith('/klant');

  const [token] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const endpoint = isKlant
    ? `${API_URL}/api/klant-portal/auth/reset-password`
    : `${API_URL}/api/auth/reset-password`;
  const loginPath = isKlant ? '/klant/login' : '/login';
  const accentColor = isKlant ? 'from-cyan-600 to-blue-600' : 'from-indigo-600 to-purple-600';

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens zijn');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('De twee wachtwoorden komen niet overeen');
      return;
    }
    if (!token) {
      toast.error('Reset-token ontbreekt in de URL');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(endpoint, { token, new_password: password });
      toast.success(r.data?.message || 'Wachtwoord bijgewerkt');
      setDone(true);
      setTimeout(() => navigate(loginPath), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Reset mislukt');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Link ongeldig</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              De reset-link mist een token. Vraag een nieuwe reset aan via de &quot;Wachtwoord vergeten&quot;-link op de loginpagina.
            </p>
            <Link to={loginPath}>
              <Button variant="outline" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Terug naar login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Nieuw wachtwoord instellen
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {isKlant ? 'Klantportaal' : 'Medewerker'} — kies een nieuw wachtwoord voor je account.
          </p>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <p className="font-medium">Wachtwoord bijgewerkt!</p>
              <p className="text-sm text-slate-500 mt-1">Je wordt zo doorgestuurd naar de login...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label>Nieuw wachtwoord</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Minimaal 8 tekens"
                  data-testid="reset-new-password"
                  autoFocus
                />
              </div>
              <div>
                <Label>Herhaal nieuw wachtwoord</Label>
                <Input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className={`w-full bg-gradient-to-r ${accentColor} text-white`}
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Bezig...' : 'Wachtwoord wijzigen'}
              </Button>
              <div className="text-center">
                <Link to={loginPath} className="text-xs text-slate-500 hover:text-slate-700">
                  Terug naar login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
